import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import apiService from "../../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";
import PhoneNumberField from "../../../../../../components/ui/PhoneNumberField";
import { validatePhone } from "../chema/validatePhone";
import { ChemaPayPalSection } from "./ChemaDonationDialog";
import EftpayPaymentForm from "../chema/EftpayPaymentForm";
import ZimswitchPaymentForm from "../chema/ZimswitchPaymentForm";
import { slugFromStreamTitle } from "../chema/slug";
import ecocashLogo from "../../../../../../assets/ecocash.png";
import paynowLogo from "../../../../../../assets/paynow.svg";
import zimswitchLogo from "../../../../../../assets/zimswitch.png";
import visaLogo from "../../../../../../assets/visa.jpg";
import paypalLogo from "../../../../../../assets/paypal.svg";
import { isDebugEnvironment } from "../../../../../../utils/env";

const ACCENT = "#155f52";

const isLocal = isDebugEnvironment();

const AMOUNTS = isLocal ? [0.01, 0.1, 0.5, 1, 5, 10] : [10, 20, 30, 40, 50, 60];
const SUGGESTED_AMOUNT = 30;
const DEFAULT_AMOUNT = isLocal ? 0.01 : 50;

type PayMethod =
  | "ecocash_paynow"
  | "ecocash_nmb"
  | "paynow"
  | "zimswitch"
  | "eftpay"
  | "card";

const PAYMENT_METHODS: { id: PayMethod; label: string; logo: string }[] = [
  { id: "ecocash_paynow", label: "EcoCash (PayNow)", logo: ecocashLogo },
  { id: "ecocash_nmb", label: "EcoCash (NMB)", logo: ecocashLogo },
  { id: "paynow", label: "Paynow", logo: paynowLogo },
  { id: "zimswitch", label: "ZimSwitch", logo: zimswitchLogo },
  { id: "eftpay", label: "Visa/Mastercard", logo: visaLogo },
  { id: "card", label: "PayPal", logo: paypalLogo },
];

export interface ContributeChemaSheetProps {
  open: boolean;
  onClose: () => void;
  streamId: number;
  streamTitle?: string;
  deceasedImage?: string;
  onDonationSuccess?: (donationId?: number) => void;
}

export default function ContributeChemaSheet({
  open,
  onClose,
  streamId,
  streamTitle,
  onDonationSuccess,
}: ContributeChemaSheetProps) {
  const [step, setStep] = useState<"details" | "method">("details");
  const [selectedAmount, setSelectedAmount] = useState<number | null>(DEFAULT_AMOUNT);
  const [customAmount, setCustomAmount] = useState(String(DEFAULT_AMOUNT));
  const [currency, setCurrency] = useState("USD");
  const [payMethod, setPayMethod] = useState<PayMethod>("ecocash_paynow");
  const [donorName, setDonorName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [pollMessage, setPollMessage] = useState("");

  const familyName = streamTitle?.replace("Stream for ", "") || "the family";

  const routeParams = useParams<{ pageType?: string }>();
  const donationPageType = routeParams.pageType || "memorial-page";
  const donationFullName = slugFromStreamTitle(streamTitle);

  const reset = () => {
    setStep("details");
    setSelectedAmount(DEFAULT_AMOUNT);
    setCustomAmount(String(DEFAULT_AMOUNT));
    setCurrency("USD");
    setPayMethod("ecocash_paynow");
    setDonorName("");
    setEmail("");
    setMessage("");
    setIsAnonymous(false);
    setPhone("");
    setPhoneError("");
    setIsProcessing(false);
    setPaymentStatus(null);
    setPollMessage("");
  };

  const handleClose = () => {
    if (isProcessing) return;
    reset();
    onClose();
  };

  const pollPaymentStatus = async (donationId: number) => {
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPaymentStatus("timeout");
        setIsProcessing(false);
        setPollMessage("Payment timed out. Please try again.");
        showErrorToast("Payment timed out. Please try again.");
        return;
      }
      attempts++;
      try {
        const res = await apiService.get<{
          paid: boolean;
          status: string;
          message: string;
        }>(`/api/chema/donations/${donationId}/status/`);

        const { paid, status: paymentStat, message: statusMessage } = res.data || {};

        if (paid) {
          setPaymentStatus("paid");
          setIsProcessing(false);
          setPollMessage(statusMessage || "Payment confirmed. Thank you!");
          showSuccessToast(statusMessage || "Payment received! Thank you.");
          onDonationSuccess?.(donationId);
          setTimeout(() => {
            reset();
            onClose();
          }, 2500);
          return;
        }

        const currentStatus = paymentStat || "pending";
        setPaymentStatus(currentStatus);
        setPollMessage(statusMessage || "Checking payment status...");

        if (currentStatus === "cancelled" || currentStatus === "failed") {
          setIsProcessing(false);
          showErrorToast(statusMessage || `Payment ${currentStatus}.`);
          return;
        }

        setTimeout(poll, 5000);
      } catch {
        setTimeout(poll, 5000);
      }
    };

    poll();
  };

  const extractErrorMessage = (error: unknown, fallback: string): string =>
    error instanceof Object &&
    "response" in error &&
    typeof (error as { response?: unknown }).response === "object" &&
    (error as { response?: unknown }).response !== null &&
    "data" in (error as { response: object }).response &&
    typeof ((error as { response: Record<string, unknown> }).response.data) === "object" &&
    (error as { response: Record<string, unknown> }).response.data !== null &&
    "error" in ((error as { response: Record<string, unknown> }).response.data as Record<string, unknown>)
      ? (((error as { response: Record<string, unknown> }).response.data as Record<string, unknown>).error as string)
      : fallback;

  const handlePayEcocash = async () => {
    if (!selectedAmount || selectedAmount <= 0) {
      showErrorToast("Please enter a donation amount greater than zero");
      return;
    }
    const phoneValidation = validatePhone(phone);
    if (phoneValidation) {
      setPhoneError(phoneValidation);
      showErrorToast(phoneValidation);
      return;
    }

    setIsProcessing(true);
    setPaymentStatus("sending");

    try {
      const response = await apiService.post<{
        success: boolean;
        donation_id: number;
        message: string;
      }>("/api/chema/donations/initiate/mobile/", {
        amount: selectedAmount,
        stream_id: streamId,
        currency,
        phone,
        method: "ecocash",
        email: email.trim() || undefined,
        message: message.trim() || undefined,
        donor_name: isAnonymous ? "" : donorName,
        is_anonymous: isAnonymous,
      });

      if (response.data?.success) {
        setPaymentStatus("awaiting_approval");
        showSuccessToast(
          response.data.message || "Check your phone to approve the payment.",
        );
        pollPaymentStatus(response.data.donation_id);
      } else {
        showErrorToast("Payment initialization failed. Please try again.");
        setIsProcessing(false);
        setPaymentStatus(null);
      }
    } catch (error: unknown) {
      showErrorToast(
        extractErrorMessage(error, "Failed to initiate payment. Please try again."),
      );
      setIsProcessing(false);
      setPaymentStatus(null);
    }
  };

  const handleProceedWithPaynow = async () => {
    if (!selectedAmount || selectedAmount <= 0) {
      showErrorToast("Please enter a donation amount greater than zero");
      return;
    }

    setIsProcessing(true);
    try {
      const response = await apiService.post<{
        success: boolean;
        redirect_url: string;
        donation_id: number;
      }>("/api/chema/donations/initiate/", {
        amount: selectedAmount,
        stream_id: streamId,
        currency,
        email: email.trim() || undefined,
        message: message.trim() || undefined,
        donor_name: isAnonymous ? "" : donorName,
        is_anonymous: isAnonymous,
      });

      if (response.data?.success && response.data.redirect_url) {
        window.location.href = response.data.redirect_url;
      } else {
        showErrorToast("Payment initialization failed. Please try again.");
        setIsProcessing(false);
      }
    } catch {
      showErrorToast("Failed to initiate payment. Please try again.");
      setIsProcessing(false);
    }
  };

  const handlePayPalPaid = (donationId?: number) => {
    setPaymentStatus("paid");
    showSuccessToast("Payment received! Thank you for your donation.");
    onDonationSuccess?.(donationId);
    setTimeout(() => {
      reset();
      onClose();
    }, 2500);
  };

  const validateForZimswitch = (): boolean => {
    if (!selectedAmount || selectedAmount <= 0) {
      showErrorToast("Please enter a donation amount greater than zero");
      return false;
    }
    return true;
  };

  const handleAmountChip = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount(String(amount));
  };

  const handleCustomAmountChange = (value: string) => {
    const v = value.replace(/[^0-9.]/g, "");
    setCustomAmount(v);
    setSelectedAmount(v.length > 0 ? parseFloat(v) : null);
  };

  const handleProceedToMethod = () => {
    if (!selectedAmount || selectedAmount <= 0) {
      showErrorToast("Please enter a donation amount greater than zero");
      return;
    }
    setStep("method");
  };

  const showStickyFooter = payMethod === "ecocash_paynow" || payMethod === "paynow";

  return (
    <AnimatePresence>
      {open && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            borderRadius: "20px",
            overflow: "hidden",
          }}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.55)",
            }}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 320 }}
            style={{
              position: "relative",
              zIndex: 1,
              maxHeight: "88%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                bgcolor: "background.paper",
                borderRadius: "20px 20px 0 0",
                display: "flex",
                flexDirection: "column",
                maxHeight: "100%",
                boxShadow: "0 -8px 30px rgba(0,0,0,0.15)",
              }}
            >
              {/* Scrollable content */}
              <Box sx={{ overflowY: "auto", px: 3, pt: 2.5, pb: 2 }}>
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.95rem", color: "text.secondary" }}>
                    {step === "method" ? (
                      <Box
                        component="span"
                        onClick={() => !isProcessing && setStep("details")}
                        sx={{ cursor: isProcessing ? "default" : "pointer", fontWeight: 700, color: "text.primary" }}
                      >
                        &larr; Back
                      </Box>
                    ) : (
                      <>
                        Your contribution helps{" "}
                        <Typography component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                          {familyName}
                        </Typography>
                      </>
                    )}
                  </Typography>
                  <IconButton onClick={handleClose} size="small" disabled={isProcessing} sx={{ mt: -0.5, mr: -0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {step === "details" && (
                <>
                {/* Amount hero */}
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "14px",
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Select
                    variant="standard"
                    disableUnderline
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    disabled={isProcessing}
                    sx={{ fontWeight: 700, color: "text.secondary", fontSize: "0.85rem" }}
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="ZWL">ZWL</MenuItem>
                  </Select>
                  <TextField
                    variant="standard"
                    value={customAmount}
                    onChange={(e) => handleCustomAmountChange(e.target.value)}
                    disabled={isProcessing}
                    InputProps={{ disableUnderline: true }}
                    inputProps={{
                      style: {
                        textAlign: "right",
                        fontSize: "1.8rem",
                        fontWeight: 700,
                        color: "text.secondary",
                      },
                    }}
                    sx={{ flex: 1, ml: 2 }}
                  />
                </Box>

                {/* Quick amounts */}
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 1.5 }}>
                  {AMOUNTS.map((amount) => (
                    <Box key={amount} sx={{ display: "flex", flexDirection: "column" }}>
                      <Button
                        onClick={() => handleAmountChip(amount)}
                        disabled={isProcessing}
                        sx={{
                          borderRadius: "12px",
                          borderColor: selectedAmount === amount ? ACCENT : "divider",
                          borderWidth: selectedAmount === amount ? 2 : 1,
                          borderStyle: "solid",
                          color: selectedAmount === amount ? ACCENT : "text.primary",
                          fontWeight: 700,
                          bgcolor: selectedAmount === amount ? "rgba(21, 95, 82, 0.08)" : "transparent",
                          textTransform: "none",
                          py: 1,
                        }}
                      >
                        ${amount}
                      </Button>
                      {amount === SUGGESTED_AMOUNT && (
                        <Typography
                          sx={{
                            textAlign: "center",
                            mt: 0.5,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            color: ACCENT,
                            bgcolor: "rgba(21, 95, 82, 0.1)",
                            borderRadius: "8px",
                            py: 0.2,
                          }}
                        >
                          SUGGESTED
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>

                {/* Anonymous */}
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={isAnonymous}
                      onChange={(e) => {
                        setIsAnonymous(e.target.checked);
                        if (e.target.checked) setDonorName("");
                      }}
                      disabled={isProcessing}
                    />
                  }
                  label={<Typography variant="body2">Donate anonymously</Typography>}
                />

                {/* Message */}
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Write a message here"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={isProcessing}
                  sx={{ mt: 1 }}
                />

                {/* Name */}
                {!isAnonymous && (
                  <TextField
                    fullWidth
                    placeholder="Your name"
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    disabled={isProcessing}
                    sx={{ mt: 1.5 }}
                  />
                )}

                {/* Email */}
                <TextField
                  fullWidth
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isProcessing}
                  sx={{ mt: 1.5 }}
                />
                </>
                )}

                {/* Payment Method */}
                {step === "method" && (
                <Box
                  sx={{
                    mt: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "14px",
                    p: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, mb: 1.5 }}>
                    Payment Method <Box component="span" sx={{ color: "error.main" }}>*</Box>
                  </Typography>

                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
                    {PAYMENT_METHODS.map((m) => (
                      <Box
                        key={m.id}
                        onClick={() => !isProcessing && setPayMethod(m.id)}
                        sx={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          py: 1.5,
                          borderRadius: 2,
                          border: "2px solid",
                          borderColor: payMethod === m.id ? ACCENT : "divider",
                          cursor: isProcessing ? "default" : "pointer",
                          opacity: isProcessing ? 0.6 : 1,
                        }}
                      >
                        {m.id === "ecocash_nmb" && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 2,
                              right: 2,
                              fontSize: "0.55rem",
                              fontWeight: 700,
                              color: "#fff",
                              bgcolor: "#0e463c",
                              borderRadius: "6px",
                              px: 0.5,
                              lineHeight: 1.4,
                            }}
                          >
                            NMB
                          </Box>
                        )}
                        <Box
                          component="img"
                          src={m.logo}
                          alt={m.label}
                          sx={{
                            height: m.id === "zimswitch" ? 32 : 22,
                            maxWidth: m.id === "zimswitch" ? 80 : 56,
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>

                  {/* Dynamic per-method content */}
                  {payMethod === "ecocash_paynow" && (
                    <PhoneNumberField
                      value={phone}
                      onChange={(value) => {
                        setPhone(value);
                        if (phoneError) setPhoneError(validatePhone(value));
                      }}
                      onBlur={() => setPhoneError(validatePhone(phone))}
                      error={!!phoneError}
                      helperText={phoneError || ""}
                      disabled={isProcessing}
                      sx={{ mt: 2 }}
                    />
                  )}

                  {payMethod === "ecocash_paynow" &&
                    paymentStatus &&
                    !["paid", "cancelled", "failed", "timeout"].includes(paymentStatus) && (
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: "info.light",
                          color: "info.contrastText",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                        }}
                      >
                        <CircularProgress size={18} />
                        <Typography variant="body2">
                          {pollMessage || "Waiting for approval on your phone..."}
                        </Typography>
                      </Box>
                    )}

                  {payMethod === "ecocash_paynow" && paymentStatus === "paid" && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: "success.light",
                        color: "success.contrastText",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {pollMessage || "Payment received! Thank you."}
                      </Typography>
                    </Box>
                  )}

                  {payMethod === "ecocash_nmb" && (
                    <Box sx={{ mt: 2 }}>
                      <EftpayPaymentForm
                        channel="ecocash"
                        amount={selectedAmount}
                        currency={currency}
                        email={email}
                        streamId={streamId}
                        pageType={donationPageType}
                        fullName={donationFullName}
                        onValidate={validateForZimswitch}
                      />
                    </Box>
                  )}

                  {payMethod === "zimswitch" && (
                    <Box sx={{ mt: 2 }}>
                      <ZimswitchPaymentForm
                        amount={selectedAmount}
                        currency={currency}
                        email={email}
                        streamId={streamId}
                        pageType={donationPageType}
                        fullName={donationFullName}
                        onValidate={validateForZimswitch}
                      />
                    </Box>
                  )}

                  {payMethod === "eftpay" && (
                    <Box sx={{ mt: 2 }}>
                      <EftpayPaymentForm
                        channel="card"
                        amount={selectedAmount}
                        currency="USD"
                        email={email}
                        streamId={streamId}
                        pageType={donationPageType}
                        fullName={donationFullName}
                        onValidate={validateForZimswitch}
                      />
                    </Box>
                  )}

                  {payMethod === "card" && (
                    <Box sx={{ mt: 2 }}>
                      <ChemaPayPalSection
                        amount={selectedAmount}
                        donorName={donorName}
                        isAnonymous={isAnonymous}
                        streamId={streamId}
                        onPaid={handlePayPalPaid}
                      />
                    </Box>
                  )}
                </Box>
                )}
              </Box>

              {/* Sticky action */}
              {step === "details" && (
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    flexShrink: 0,
                  }}
                >
                  <Button
                    fullWidth
                    onClick={handleProceedToMethod}
                    disabled={!selectedAmount}
                    sx={{
                      bgcolor: ACCENT,
                      color: "#fff",
                      borderRadius: "999px",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      py: 1.4,
                      "&:hover": { bgcolor: "#0e463c" },
                    }}
                  >
                    Proceed to Pay
                  </Button>
                </Box>
              )}

              {step === "method" && showStickyFooter && (
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    flexShrink: 0,
                  }}
                >
                  <Button
                    fullWidth
                    onClick={payMethod === "ecocash_paynow" ? handlePayEcocash : handleProceedWithPaynow}
                    disabled={isProcessing || !selectedAmount}
                    sx={{
                      bgcolor: ACCENT,
                      color: "#fff",
                      borderRadius: "999px",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      py: 1.4,
                      "&:hover": { bgcolor: "#0e463c" },
                    }}
                  >
                    {isProcessing ? (
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    ) : (
                      "Pay Now"
                    )}
                  </Button>
                </Box>
              )}
            </Box>
          </motion.div>
        </Box>
      )}
    </AnimatePresence>
  );
}